const DB_NAME = 'secshot-video-cache';
const DB_VERSION = 1;
const STORE_NAME = 'videos';
const MAX_ENTRIES = 5;

type VideoRecord = {
  episodeId: string;
  blob: Blob;
  updatedAt: number;
};

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') {
    return Promise.resolve(null);
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'episodeId' });
        store.createIndex('updatedAt', 'updatedAt');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      const requestError = request.error;
      if (requestError instanceof Error) {
        reject(requestError);
        return;
      }
      reject(new Error('Failed to open video cache DB'));
    };
  });
}

export async function loadCachedVideo(episodeId: string): Promise<Blob | null> {
  if (!episodeId) return null;
  let db: IDBDatabase | null = null;
  try {
    db = await openDb();
  } catch {
    return null;
  }
  if (!db) return null;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(episodeId);
    request.onsuccess = () => {
      const record = request.result as VideoRecord | undefined;
      if (!record?.blob) {
        resolve(null);
        return;
      }
      record.updatedAt = Date.now();
      store.put(record);
      resolve(record.blob);
    };
    request.onerror = () => resolve(null);
    tx.oncomplete = () => {
      db?.close();
    };
    tx.onerror = () => {
      db?.close();
    };
  });
}

export async function storeCachedVideo(episodeId: string, blob: Blob): Promise<void> {
  if (!episodeId) return;
  let db: IDBDatabase | null = null;
  try {
    db = await openDb();
  } catch {
    return;
  }
  if (!db) return;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({ episodeId, blob, updatedAt: Date.now() } satisfies VideoRecord);
    const getAllRequest = store.getAll();
    getAllRequest.onsuccess = () => {
      const records = (getAllRequest.result || []) as VideoRecord[];
      if (records.length <= MAX_ENTRIES) return;
      records.sort((a, b) => a.updatedAt - b.updatedAt);
      const overflow = records.length - MAX_ENTRIES;
      for (let i = 0; i < overflow; i += 1) {
        const rec = records[i];
        if (rec?.episodeId) {
          store.delete(rec.episodeId);
        }
      }
    };
    tx.oncomplete = () => {
      db?.close();
      resolve();
    };
    tx.onerror = () => {
      db?.close();
      resolve();
    };
  });
}
